/**
 * Athos Beacon 2.0 analytics tracker
 *
 * Beacon 2.0 is a reference-based standard: each event sends a small `{ context, data }` envelope
 * to a per-event endpoint, referencing results by `responseId` + `uid`/`parentId`
 * rather than embedding full product mappings.
 *
 * Endpoints (one family per surface type):
 *   {origin}/{siteId}/{search|autocomplete|category|recommendations}/{render|impression|clickthrough}
 *
 * Only the recommendations family carries a `data.tag` (the profile); the other
 * surfaces reference the interaction by `responseId` alone.
 *
 * Docs: https://docs.athoscommerce.com/reference/search-tracking-guide (and the
 * autocomplete/category/recommendations tracking guides alongside it)
 */

import { session } from '@/components/helpers/session'
import { globalState } from '@/components/helpers/utils'
import { getBeaconOrigin, getAthosSiteID } from 'libs/athos/athos-utils'
import type { AthosResult } from 'libs/athos/athos.types'

export const BEACON_EVENTS = {
  RENDER: 'render',
  IMPRESSION: 'impression',
  CLICKTHROUGH: 'clickthrough'
}

export type BeaconSurface = 'search' | 'autocomplete' | 'category' | 'recommendations'

// Identifies this integration as the source of beacon events.
const BEACON_INITIATOR = 'athos/review-theme/2.0'

// text/plain avoids a CORS preflight on the beacon POST.
const BEACON_CONTENT_TYPE = 'text/plain'

/**
 * Expose the last N beacon events on `window.__athosBeacon` so a clickthrough can be
 * audited without a HAR export. `sendBeacon` gives JS no access to the HTTP response,
 * so this records what was dispatched and whether the browser accepted it for queueing
 * — the delivery receipt itself only exists on the Athos ingestion side.
 * Persisted in sessionStorage so entries survive the navigation the click triggers.
 */
const BEACON_LOG_KEY = '__athosBeaconLog'
const BEACON_LOG_LIMIT = 50

function recordBeacon(entry: Record<string, unknown>): void {
  try {
    const log = JSON.parse(window.sessionStorage.getItem(BEACON_LOG_KEY) ?? '[]')
    log.push(entry)
    window.sessionStorage.setItem(BEACON_LOG_KEY, JSON.stringify(log.slice(-BEACON_LOG_LIMIT)))
  } catch {
    // Storage unavailable (private mode / quota) — auditing must never break tracking.
  }
}

declare global {
  interface Window {
    __athosBeacon?: {
      log: () => unknown[]
      table: () => void
      json: () => string
      clear: () => void
    }
  }
}

if (typeof window !== 'undefined') {
  const log = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(BEACON_LOG_KEY) ?? '[]')
    } catch {
      return []
    }
  }

  window.__athosBeacon = {
    log,
    table: () =>
      console.table(
        log().map((entry: Record<string, any>) => ({
          event: entry.event,
          surface: entry.url?.split('/').at(-2),
          // `status` for the fetch path; `queued` for sendBeacon, which never exposes a response.
          status: entry.status ?? `queued: ${entry.queued}`,
          responseId: entry.payload?.data?.responseId,
          results: entry.payload?.data?.results?.map((r: any) => r.parentId).join(', '),
          page: entry.payload?.context?.pageUrl
        }))
      ),
    json: () => JSON.stringify(log(), null, 2),
    clear: () => window.sessionStorage.removeItem(BEACON_LOG_KEY)
  }
}

export class AthosBeacon {
  type: BeaconSurface
  profile?: string
  responseId?: string
  siteId: string

  /**
   * @param type — the Beacon surface family this tracker reports to
   * @param profile — the recommendation profile tag (`data.tag`); recommendations only
   * @param responseId — the Athos API `responseId` for this surface
   */
  constructor(type: BeaconSurface = 'recommendations', profile?: string, responseId?: string) {
    this.type = type
    this.profile = profile
    this.responseId = responseId
    this.siteId = getAthosSiteID()
  }

  setResponseId(responseId: string): void {
    this.responseId = responseId
  }

  endpoint(eventType: string): string {
    return `${getBeaconOrigin()}/${this.siteId}/${this.type}/${eventType}`
  }

  /**
   * Shared Beacon 2.0 context envelope.
   * `website.trackingCode` (Beacon 1.0) is removed in 2.0.
   */
  generateContext() {
    const shopperId = session.shopper.get()
    const currencyCode = globalState.localization?.currency

    return {
      timestamp: new Date().toISOString(), // RFC3339 / UTC
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
      userId: session.userId.get(),
      sessionId: session.sessionId.get(),
      pageLoadId: session.pageLoadId.get(),
      initiator: BEACON_INITIATOR,
      ...(shopperId && { shopperId }),
      ...(currencyCode && { currency: { code: currencyCode } })
    }
  }

  /**
   * Reference an individual result by type + ids only (reference-based tracking).
   * `uid` is the variant id, `parentId` the product id. When the feed only exposes
   * a product-level id we fall back to it for both — Beacon requires both to be
   * present and non-empty.
   */
  buildResultReference(result: AthosResult) {
    const uid = result.uid ?? result.id
    const parentId = result.parentId ?? result.groupId ?? uid
    return {
      type: 'product',
      uid: `${uid}`,
      parentId: `${parentId}`
    }
  }

  /**
   * POST a beacon event. Clickthrough uses `sendBeacon`/`keepalive` so it survives
   * the navigation triggered by the click.
   */
  send(eventType: string, data: Record<string, unknown>, { keepalive = false } = {}): void | Promise<unknown> {
    if (!this.siteId || !this.responseId) return

    const payload = {
      context: this.generateContext(),
      data: {
        // Only the recommendations surface identifies its profile via `tag`.
        ...(this.profile && { tag: this.profile }),
        responseId: this.responseId,
        ...data
      }
    }

    const url = this.endpoint(eventType)
    const body = JSON.stringify(payload)

    if (keepalive && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      // Send the body as a string, not a Blob. Both produce a CORS-safelisted
      // `text/plain` POST, but with a Blob the payload is unreadable in DevTools once
      // the click navigation tears the page down (DevTools resolves a blob body lazily,
      // and the originating renderer is gone by then). A Blob dispatched *without* a
      // following navigation does show its payload, so it is the combination that bites.
      // Verified before/after against the deployed theme — Athos QA, RR-9 2026-07-21.
      const queued = navigator.sendBeacon(url, body)
      recordBeacon({ timestamp: payload.context.timestamp, event: eventType, url, queued, payload })
      return
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': BEACON_CONTENT_TYPE },
      body,
      keepalive
    })
      .then((response) => {
        recordBeacon({ timestamp: payload.context.timestamp, event: eventType, url, status: response.status, payload })
      })
      .catch(() => {
        // Tracking must never break the storefront.
      })
  }

  /**
   * Render — fired once when a recommendation surface is rendered.
   * Payload: { data: { tag, responseId } }
   */
  render() {
    return this.send(BEACON_EVENTS.RENDER, {})
  }

  /**
   * Impression — the visible recommendation results.
   * Personalization surfaces are permitted to batch the visible array in a single
   * impression event (rather than per-card).
   * Payload: { data: { tag, responseId, results: [{type,uid,parentId}], banners: [] } }
   */
  impression(results: AthosResult[]) {
    if (!Array.isArray(results) || results.length === 0) return
    return this.send(BEACON_EVENTS.IMPRESSION, {
      results: results.map((result) => this.buildResultReference(result)),
      banners: []
    })
  }

  /**
   * Clickthrough — a single clicked result.
   * Payload: { data: { tag, responseId, results: [{type,uid,parentId}] } }
   */
  clickthrough(result: AthosResult) {
    if (!result) return
    return this.send(
      BEACON_EVENTS.CLICKTHROUGH,
      { results: [this.buildResultReference(result)] },
      { keepalive: true }
    )
  }
}
